import { Avatar, Button } from '@digibee/beehive-ui';
import { choose, iff, otherwise, when } from '@digibee/control-statements';
import Flow, { FlowSpec } from '@digibee/flow';
import { Copy, PasteClipboard, ThumbsDown, ThumbsUp } from 'iconoir-react';
import { cloneDeep } from 'lodash/fp';
import Markdown from 'markdown-to-jsx';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import FlowTreeErrorBoundary from './FlowTreeErrorBoundary';
import * as Elements from './Message.elements';
import { FeedbackParams } from '../../AIChat';
import { AIMessageData, ChatType } from '../../AIChat.machine';
import LoadingBar from '../LoadingBar/LoadingBar';

import { API_KEY } from '~/common/helpers/environment';
import i18n from '~/common/helpers/i18n';
import WebAnalytics from '~/common/helpers/webAnalytics';
import applicationTheme from '~/common/styled/theme';
import { sendToBus } from '~/scenes/Build/components/Bus';
import { getConnector } from '~/scenes/Build/helpers';
import FlowTree from '~/scenes/Build/scenes/FlowBuilder/components/Design/panels/Tree/components/FlowTree';
import { ShapeType } from '~/scenes/Build/scenes/FlowBuilder/components/Design/panels/Tree/components/FlowTree/Tree';
import { useComponentConfigStore } from '~/scenes/Build/scenes/FlowBuilder/stores';
import { RootState } from '~/scenes/Monitor/scenes/Metrics/models/types';

const useMapConnectorImage = () =>
  useCallback(
    (
      item: string,
      type?: string
    ): {
      shape: ShapeType;
      image: string;
    } => {
      const nonExistentConnector = getConnector('non-existent');
      const nonExistentConnectorData = {
        shape: nonExistentConnector.shape as ShapeType,
        image: nonExistentConnector.image
      };

      if (!item) return nonExistentConnectorData;

      if (type === 'trigger') {
        const trigger = getConnector('trigger', item);

        return {
          shape: trigger.shape as ShapeType,
          image: trigger.image
        };
      }

      const itemConfig = useComponentConfigStore.getState().getConfig(item);

      if (!itemConfig) return nonExistentConnectorData;

      const connector = getConnector(
        itemConfig.type,
        (itemConfig.id.includes('api-first:') ||
          itemConfig.id.includes('mcp-server:')) &&
          (itemConfig.type === 'choice' || itemConfig.type === 'tool-router')
          ? itemConfig.id
          : itemConfig?.name
      );

      if (connector) {
        return {
          shape: connector.shape as ShapeType,
          image: connector.image
        };
      }

      return nonExistentConnectorData;
    },
    []
  );

const getStartDisconnectedTrackName = (flowSpec: FlowSpec): string => {
  const trackName = Object.keys(flowSpec).find(key =>
    key.startsWith('disconnected-root')
  );
  return trackName || 'start';
};

const fixFlowSpecStartTrack = (flowSpec: FlowSpec): FlowSpec => {
  const clonnedFlowspec = cloneDeep(flowSpec) as FlowSpec;

  const trackName = getStartDisconnectedTrackName(clonnedFlowspec);
  clonnedFlowspec.start = clonnedFlowspec[trackName];

  delete clonnedFlowspec[trackName];

  return clonnedFlowspec;
};

const calcPreviewHeight = (flow: Flow): number => {
  const lineHeight = 25;
  return flow.components().length * lineHeight + lineHeight;
};

type MessageProps = {
  text?: string;
  sender?: string;
  image?: string;
  loading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  noImage?: boolean;
  chatType?: ChatType;
  data?: AIMessageData;
  feedback?: 'like' | 'dislike';
  chatIndex?: number;
  messageIndex?: number;
  onFeedbackClick?: (props: FeedbackParams) => void;
  isDetachedChat?: boolean;
};

function Message({
  sender,
  text,
  loading,
  isError,
  noImage,
  chatType,
  data,
  onRetry,
  chatIndex,
  feedback,
  messageIndex,
  isDetachedChat,
  onFeedbackClick
}: MessageProps) {
  const flowSpec = data?.pipe_gen?.flowspec;

  const profile = useSelector(
    (state: RootState) =>
      state.profile as {
        user: { email: string; imageUrl: string };
        avatarTimestamp: number;
      }
  );

  const token = useSelector(
    (state: RootState) => state.authentication.userData?.token
  );

  const flow = useMemo(
    () => new Flow(flowSpec ? fixFlowSpecStartTrack(flowSpec) : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const mapConnectorImage = useMapConnectorImage();

  const insertOnCanvasAction = () => {
    sendToBus({
      event: {
        type: 'PASTE',
        value: JSON.stringify({ flowSpec, meta: {} })
      },
      to: 'design'
    });
    // @ts-expect-error
    const { realm } = window.store.getState().application.realm;
    const { email } =
      // @ts-expect-error
      window.store.getState().authentication.userData;
    WebAnalytics.sendEvent(
      '[AI] (AI Assistant Chat) On click insert flowspec on canvas',
      {
        realm,
        user: email,
        timestamp: Date.now()
      }
    );
  };

  const copyFlowspecActions = () => {
    navigator.clipboard.writeText(JSON.stringify({ flowSpec, meta: {} }));
    // @ts-expect-error
    const { realm } = window.store.getState().application.realm;
    const { email } =
      // @ts-expect-error
      window.store.getState().authentication.userData;
    WebAnalytics.sendEvent('[AI] (AI Assistant Chat) On click copy flowspec', {
      realm,
      user: email,
      timestamp: Date.now()
    });
  };

  const copyJoltSpec = () => {
    navigator.clipboard.writeText(JSON.stringify(data?.jolt, null, 2));
  };

  return (
    <Elements.MessageContainer>
      {iff(!noImage, () => (
        <Elements.UserImageWrapper>
          {choose(
            when(sender === 'user', () => (
              <Avatar
                data-testid='profile-show-menu-button'
                size='small'
                text={profile.user?.email}
                placeholder={profile.user?.email}
                type={profile.user?.imageUrl ? 'picture' : 'char'}
                config={{
                  provider: {
                    url: `${profile.user?.imageUrl}?${profile.avatarTimestamp}`,
                    options: {
                      responseType: 'blob',
                      headers: {
                        apiKey: API_KEY,
                        Authorization: token
                      }
                    }
                  }
                }}
              />
            )),
            otherwise(() => (
              <Elements.AIImageContainer>
                <Elements.AIImage
                  src={applicationTheme.images.iconLogoDigibee}
                  alt='AI'
                />
              </Elements.AIImageContainer>
            ))
          )}
        </Elements.UserImageWrapper>
      ))}
      <Elements.MessageWrapper>
        <Elements.MessageContent>
          {choose(
            when(!!isError, () => (
              <>
                An error occured when we tried to process your request.
                <Elements.Link onClick={onRetry}> Try again</Elements.Link>
              </>
            )),
            when(!!loading, () => <LoadingBar />),
            otherwise(() => (
              <>
                {sender === 'ai-assistant' &&
                  chatType &&
                  ['pipeline-generator', 'pipeline-assistant'].includes(
                    chatType
                  ) && (
                    <FlowTreeErrorBoundary>
                      <Elements.FlowTreeContainer
                        height={calcPreviewHeight(flow)}
                      >
                        <FlowTree
                          customFlow={flow}
                          currentLevelName='start'
                          mapConnectorImage={mapConnectorImage}
                          withoutStarterNode
                        />
                      </Elements.FlowTreeContainer>
                    </FlowTreeErrorBoundary>
                  )}

                {sender === 'user' &&
                  chatType === 'jolt-assistant' &&
                  data !== undefined && (
                    <>
                      <Elements.InnerLabel>
                        {i18n.t('label.ai_jolt_generator_input')}:
                      </Elements.InnerLabel>
                      <Elements.Pre>{data.input_text}</Elements.Pre>
                      <Elements.InnerLabel>
                        {' '}
                        {i18n.t('label.ai_jolt_generator_output')}:
                      </Elements.InnerLabel>
                      <Elements.Pre>{data.output_text}</Elements.Pre>
                    </>
                  )}

                {sender === 'ai-assistant' &&
                  chatType === 'jolt-assistant' &&
                  data?.jolt && (
                    <>
                      <Elements.InnerLabel>
                        {i18n.t('label.ai_jolt_generator_jolt_spec')}:
                      </Elements.InnerLabel>
                      <Elements.Pre>
                        {JSON.stringify(data?.jolt, null, 2)}
                      </Elements.Pre>
                    </>
                  )}
                <Elements.MarkdownWrapper>
                  <Markdown>{text || ''}</Markdown>
                </Elements.MarkdownWrapper>
                {sender === 'ai-assistant' && (
                  <Elements.LikeAndDislikeWrapper>
                    <Elements.ButtonLikeWrapper
                      className={feedback === 'like' ? 'active' : ''}
                    >
                      <ThumbsUp
                        className='icon-large'
                        onClick={() =>
                          onFeedbackClick?.({
                            chatIndex,
                            chatType,
                            feedback: 'like',
                            messageIndex,
                            isDetachedChat
                          })
                        }
                      />
                    </Elements.ButtonLikeWrapper>
                    <Elements.ButtonDislikeWrapper
                      className={feedback === 'dislike' ? 'active' : ''}
                    >
                      <ThumbsDown
                        className='icon-large'
                        onClick={() =>
                          onFeedbackClick?.({
                            chatIndex,
                            chatType,
                            feedback: 'dislike',
                            messageIndex,
                            isDetachedChat
                          })
                        }
                      />
                    </Elements.ButtonDislikeWrapper>
                  </Elements.LikeAndDislikeWrapper>
                )}
              </>
            ))
          )}
        </Elements.MessageContent>
        {sender === 'ai-assistant' &&
          chatType &&
          ['pipeline-generator', 'pipeline-assistant'].includes(chatType) && (
            <Elements.MessageActions>
              <Button outlined size='small' onClick={copyFlowspecActions}>
                <Elements.ActionIconWrapper>
                  <Copy />
                </Elements.ActionIconWrapper>
                {i18n.t('label.ai_assistant_action_copy')}
              </Button>
              <Button outlined size='small' onClick={insertOnCanvasAction}>
                <Elements.ActionIconWrapper>
                  <PasteClipboard />
                </Elements.ActionIconWrapper>
                {i18n.t('label.ai_assistant_action_insert')}
              </Button>
            </Elements.MessageActions>
          )}

        {sender === 'ai-assistant' && chatType === 'jolt-assistant' && (
          <Elements.MessageActions>
            <Button outlined size='small' onClick={copyJoltSpec}>
              <Elements.ActionIconWrapper>
                <Copy />
              </Elements.ActionIconWrapper>
              {i18n.t('label.ai_assistant_action_copy')}
            </Button>
          </Elements.MessageActions>
        )}
      </Elements.MessageWrapper>
    </Elements.MessageContainer>
  );
}

export default Message;
